import React, { useMemo, useState } from 'react';
import { Table, Button, Popconfirm, Input, Space } from 'antd';
import { useSupplierPayment } from '../../store/supplierPaymentContext';
import { SupplierPayment } from '../../types/supplierPayment';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import PaymentInvoiceHandler from '../Print/PaymentInvoiceHandler';
import PageNavigation from '../Layout/PageNavigation';

interface Props {
    onEdit: (supplierPayment: SupplierPayment) => void;
}
const PAYMENT_METHODS = [
    { value: "cash", label: "Cash" },
    { value: "bank", label: "Bank" },
    { value: "other", label: "Other" },
];
const SupplierPaymentList: React.FC<Props> = ({ onEdit }) => {
    const { supplierPayments, deleteSupplierPayment } = useSupplierPayment();
    const { t } = useTranslation();
    const [search, setSearch] = useState<string>('');

    const filtered = useMemo(() => {
        return supplierPayments.filter(r =>
            (r.paymentNumber ?? '').toLowerCase().includes(search.toLowerCase()) ||
            (r.supplierName ?? '').toLowerCase().includes(search.toLowerCase()) ||
            (r.supplierPhone ?? '').toLowerCase().includes(search.toLowerCase())
        );
    }, [supplierPayments, search]);
    const getCustomerName = (customerId?: string, customerName?: string) => {
        // If you want to fetch from customers context, add it here.
        return customerName || "-";
    };
    const getPaymentMethodLabel = (code?: string) => {
        return PAYMENT_METHODS.find(m => m.value === code)?.label || "-";
    };

    const columns = [
        { title: t('paymentNumber'), dataIndex: 'paymentNumber' },
        { title: t('supplier'), dataIndex: 'supplierName' },
        { title: t('supplierPhone'), dataIndex: 'supplierPhone' },
        { title: t('date'), dataIndex: 'date', render: (d: string) => new Date(d).toLocaleDateString() },
        { title: t('amount'), dataIndex: 'amount' },
        { title: t('currency'), dataIndex: 'currency' },
        { title: t('paymentMethod'), dataIndex: 'paymentMethod' },
        { title: t('reference'), dataIndex: 'reference' },
        { title: t('note'), dataIndex: 'note' },
        {
            title: t('actions'),
            render: (_: any, record: SupplierPayment) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => onEdit(record)}>{t('edit')}</Button>
                    <Popconfirm
                        title={t('sure_to_delete') as string}
                        onConfirm={() => deleteSupplierPayment(record.id!)}
                    >
                        <Button danger icon={<DeleteOutlined />}>{t('delete')}</Button>
                    </Popconfirm>
                    <PaymentInvoiceHandler
                        type="loan"
                        invoice={record}
                        getPartyName={() =>
                            getCustomerName(record.supplier, record.supplierName)
                        }
                        getPaymentMethodLabel={getPaymentMethodLabel}
                        buttonText={t('print_invoice') as string}
                    />
                </Space>
            )
        }
    ];

    return (
        <div>
            <PageNavigation title={t('supplier_payments') as string} />
            <Input.Search
                allowClear
                style={{ marginBottom: 16, maxWidth: 300 }}
                placeholder={t('search_payments') as string}
                onChange={e => setSearch(e.target.value)}
                value={search}
            />
            <Table
                rowKey={record => record.id as string}
                dataSource={filtered}
                columns={columns}
                pagination={{ pageSize: 8 }}
                scroll={{ x: 'max-content' }}
            />
        </div>
    );
};

export default SupplierPaymentList;
